import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { AppMenuitem } from './app.menuitem';
import { AuthService } from '@/app/features/authentication/services/auth.service';
import { IngestionService } from '@/app/features/files/pages/upload/services/ingestion.service';


export interface MyAppMenuItem extends MenuItem {
    roles?: string[];
    permissions?: string[];
    requireAll?: boolean;
}

@Component({
    selector: 'app-menu',
    standalone: true,
    imports: [CommonModule, AppMenuitem, RouterModule],
    template: `
    @if (authenticated()) {

            <ul class="layout-menu">

                @for (item of model(); track item.label) {

                    @if (!item.separator) {

                        <li
                            app-menuitem
                            [item]="item"
                            [root]="item.label === 'DASHBOARD' || !!item.items?.length">
                        </li>

                    } @else {

                        <li class="menu-separator"></li>

                    }

                }

            </ul>
        }
    `,
})
export class AppMenu {
    readonly authService =
        inject(AuthService);

    private readonly ingestionService = inject(
        IngestionService,
    );
    manualUploadEnabled = false;

    readonly authenticated =
        this.authService.authenticatedSignal;

    constructor() {
        this.ingestionService.loadManualUploadEnabled();
        this.ingestionService.loadSeedExportEnabled();
    }

    private readonly allMenuItems: MyAppMenuItem[] = [
        {
            label: 'DASHBOARD',
            icon: '',
            routerLink: ['/dashboard'],

        },
        {
            label: 'Administration',

            items: [

                {
                    label: 'Users',
                    icon: 'pi pi-fw pi-user',
                    routerLink: ['/users'],

                    permissions: [
                        'security.user.view'
                    ],
                },
                {
                    label: 'Roles',
                    icon: 'pi pi-shield',
                    routerLink: ['/roles'],

                    permissions: [
                        'security.role.view'
                    ]
                },
            ],

            roles: [
                'system.administrator'
            ]
        },

        {
            label: 'TRANSFORMATION RULES',

            items: [
                {
                    label: 'EDITOR',
                    icon: 'pi pi-fw pi-id-card',
                    routerLink: ['/rules'],
                    roles: [
                        'system.administrator', 'rule_engine.rule_designer', 'rule_engine.rule_operator'
                    ]
                },
                {
                    label: 'Seed export',
                    icon: 'pi pi-fw pi-id-card',
                    routerLink: ['/seed-export'],
                    roles: [
                        'system.administrator',
                    ]
                },
                {
                    label: 'Seed import',
                    icon: 'pi pi-fw pi-id-card',
                    routerLink: ['/seed-import'],
                    roles: [
                        'system.administrator'
                    ]
                },
            ],
            roles: [
                'system.administrator', 'rule_engine.rule_designer', 'rule_engine.rule_operator'
            ]
        },
        {
            label: 'FILE PROCESSING',

            items: [

                {
                    label: 'Upload',
                    icon: 'pi pi-fw pi-id-card',
                    routerLink: ['/upload'],
                    roles: [
                        'system.administrator', 'rule_engine.rule_designer', 'rule_engine.rule_operator'
                    ]
                },              
            ],
            roles: [
                'system.administrator', 'rule_engine.rule_designer', 'rule_engine.rule_operator'
            ]
        }

    ];

    readonly model = computed(() => {

        if (this.authenticated() !== true) {
            return [];
        }

        return this.filterMenu(
            this.allMenuItems
        );
    });




    private hasAccess(item: MyAppMenuItem): boolean {

        if (
            item.routerLink?.[0] === '/upload' &&
            !this.ingestionService.manualUploadEnabled()
        ) {
            return false;
        }
        if (
            item.routerLink?.[0] === '/seed-export' &&
            !this.ingestionService.seedExportEnabled()
        ) {
            return false;
        }

        // Nema nikakvih ograničenja -> javno dostupan menu item
        if (!item.roles?.length && !item.permissions?.length) {
            return true;
        }

        // Role provjera
        if (item.roles?.length) {
            const roleMatch = item.requireAll
                ? item.roles.every(role =>
                    this.authService.hasRole(role)
                )
                : item.roles.some(role =>
                    this.authService.hasRole(role)
                );

            if (!roleMatch) {
                return false;
            }
        }

        // Permission provjera
        if (item.permissions?.length) {
            const permissionMatch = item.requireAll
                ? item.permissions.every(permission =>
                    this.authService.hasPermission(permission)
                )
                : item.permissions.some(permission =>
                    this.authService.hasPermission(permission)
                );

            if (!permissionMatch) {
                return false;
            }
        }

        return true;
    }

    private filterMenu(
        items: MyAppMenuItem[]
    ): MyAppMenuItem[] {

        return items
            .filter(item => this.hasAccess(item))
            .map(item => {

                if (!item.items?.length) {
                    return item;
                }

                const children = this.filterMenu(
                    item.items as MyAppMenuItem[]
                );

                return {
                    ...item,
                    items: children
                };
            })
            .filter(item =>
                !item.items || item.items.length > 0
            );
    }
}
